import { useState, useEffect, useCallback, useMemo } from 'react';

import type { RovoLogo } from '@atlassian/rovo-connectors-config/icons';
import { SMARTLINKS_RESOURCE_TYPE_PREFIX } from '@atlassian/rovo-connectors-config/schemas/types';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import type { ProductKeys3P as ProductKeys3PType } from '@atlassian/rovo-connectors-config/types';
import { ThirdPartyConfigs } from '@atlassian/rovo-connectors-config/utils';
import {
	get3pSearchConfig,
	update3pConfigCacheEntry,
} from '@atlassian/search-client/fetch-search-config';
import type { T3pResource } from '@atlassian/search-client/fetch-search-config';

import type { SmartLinkEnv } from '../../utils/outboundAuth';
import {
	buildSmartLinkAuthUrl,
	detectEnvFromPayload,
	fetchOutboundAuthAccounts,
	hasSmartLinkContainerMapping,
	isAuthenticatedForProduct,
	type OutboundAuthAccountsPayload,
} from '../../utils/outboundAuth';
import { isValidProductKey } from '../../utils/productKeys';
import {
	resolveRecommendedProductKeys,
	type SmartlinkConnectorRecommendationInputs,
} from '../../utils/smartlink-connector-recommendations';

const DEFAULT_MAX_CONNECTORS = 3;

// The full prefix used in smartlinks connector resource type strings, e.g. 'smartlinks-connector-gdrive'
const SMARTLINKS_CONNECTOR_TYPE_PREFIX = `${SMARTLINKS_RESOURCE_TYPE_PREFIX}-`;

// Dummy/test product keys that should never be shown to users. See https://hello.atlassian.net/wiki/spaces/rdm/pages/6375959349/Knowledge+base#Connectors for details
const DUMMY_SMARTLINKS_PRODUCT_KEYS = new Set(['gdrive-rsl']);

const normalizeSmartlinksProductKey = (productKey: string): string =>
	productKey === 'gdrive' ? ProductKeys3P.GoogleDrive : productKey;

export interface ConnectorInfo {
	authUrl: string;
	displayName: string;
	icon: RovoLogo | undefined;
	isAuthenticated: boolean;
	productKey: string;
	requiresOauth: boolean;
}

export interface Use3pConnectorResourcesResult {
	connectorResources: ConnectorInfo[];
	isLoading: boolean;
	onConnectorConnected: (productKey: string) => void;
}

/**
 * Builds the fallback connector list shown when the API returns zero valid
 * smartlinks connector resources.
 *
 * App selection comes straight from `resolveRecommendedProductKeys`, which
 * guarantees Tier 3 (HARDCODED_DEFAULTS) is always populated — so no
 * parallel "default app list" is maintained here.
 *
 * Each connector's `authUrl` and `isAuthenticated` come from the
 * outbound-auth ground truth (`buildSmartLinkAuthUrl` /
 * `isAuthenticatedForProduct`). Providers without a canonical container
 * mapping are filtered out so we never render a broken Connect button.
 */
const buildFallbackConnectors = async (
	maxConnectors: number,
	outboundAuthPayload: OutboundAuthAccountsPayload | null,
	recommendedProductKeys: ReadonlyArray<ProductKeys3PType>,
	env: SmartLinkEnv | undefined,
): Promise<ConnectorInfo[]> => {
	const mappedProductKeys = recommendedProductKeys
		// Drop providers that have no canonical container mapping — rendering a
		// Connect button for them would either 404 or open the wrong OAuth flow.
		.filter((productKey) => hasSmartLinkContainerMapping(productKey, env))
		.slice(0, maxConnectors);

	const resolved: Array<ConnectorInfo | null> = await Promise.all(
		mappedProductKeys.map(async (productKey) => {
			const staticConfig = await ThirdPartyConfigs.get(productKey);
			const authUrl = buildSmartLinkAuthUrl(productKey, env);

			if (!authUrl) {
				// Defensive: `hasSmartLinkContainerMapping` already filtered these
				// out, but if anything slipped through we'd rather drop the entry
				// than emit a broken Connect URL.
				return null;
			}

			const connector: ConnectorInfo = {
				productKey,
				displayName: String(staticConfig?.displayName || productKey),
				authUrl,
				icon: staticConfig?.icon,
				requiresOauth: staticConfig?.requiresOauth ?? true,
				isAuthenticated: isAuthenticatedForProduct(outboundAuthPayload, productKey, env),
			};
			return connector;
		}),
	);

	return resolved.filter((entry): entry is ConnectorInfo => entry !== null);
};

/**
 * Derives the product key from a smartlinks connector resource type string
 * by stripping the 'smartlinks-connector-' prefix.
 *
 * e.g. 'smartlinks-connector-gdrive' → 'gdrive'
 *      'smartlinks-connector-gdrive-rsl' → 'gdrive-rsl'
 */
const deriveProductKeyFromSmartlinksResourceType = (resourceType: string): string =>
	resourceType.replace(SMARTLINKS_CONNECTOR_TYPE_PREFIX, '');

/**
 * Filters a flat list of T3pResource entries down to valid smartlinks connector
 * resources: those whose type starts with 'smartlinks-connector-', have an
 * outboundAuthUrl, and are not dummy/test products.
 */
const isMatchingConnectorResource = (resource: T3pResource, productKey: string): boolean => {
	if (resource.type === productKey) {
		return true;
	}
	if (resource.type === `${SMARTLINKS_CONNECTOR_TYPE_PREFIX}${productKey}`) {
		return true;
	}
	return resource.type.includes(productKey);
};

const filterSmartlinksConnectorResources = (allResources: T3pResource[]): T3pResource[] =>
	allResources.filter((resource) => {
		if (!resource.type.startsWith(SMARTLINKS_CONNECTOR_TYPE_PREFIX)) {
			return false;
		}
		if (!resource.outboundAuthUrl) {
			return false;
		}
		const derivedProductKey = deriveProductKeyFromSmartlinksResourceType(resource.type);
		if (DUMMY_SMARTLINKS_PRODUCT_KEYS.has(derivedProductKey)) {
			return false;
		}
		return true;
	});

const orderSmartlinksConnectorResources = (
	resources: ReadonlyArray<T3pResource>,
	recommendedProductKeys: ReadonlyArray<ProductKeys3PType>,
	maxConnectors: number,
): T3pResource[] => {
	if (recommendedProductKeys.length === 0) {
		return resources.slice(0, maxConnectors);
	}

	const selectedResources: T3pResource[] = [];
	const selectedIndexes = new Set<number>();

	for (const productKey of recommendedProductKeys) {
		const resourceIndex = resources.findIndex(
			(resource, index) =>
				!selectedIndexes.has(index) && isMatchingConnectorResource(resource, productKey),
		);

		if (resourceIndex >= 0) {
			selectedResources.push(resources[resourceIndex]);
			selectedIndexes.add(resourceIndex);
		}

		if (selectedResources.length >= maxConnectors) {
			return selectedResources;
		}
	}

	for (const [index, resource] of resources.entries()) {
		if (!selectedIndexes.has(index)) {
			selectedResources.push(resource);
		}

		if (selectedResources.length >= maxConnectors) {
			return selectedResources;
		}
	}

	return selectedResources;
};

/**
 * Maps a smartlinks connector T3pResource to a ConnectorInfo object, resolving
 * static display info (icon, displayName, requiresOauth) from ThirdPartyConfigs.
 *
 * Connection state and auth URL use a layered preference:
 *   - `isAuthenticated`: outbound-auth ground truth wins when available;
 *     fall back to `smartlinksResource.isUserOAuthed` otherwise.
 *   - `authUrl`: CDS's `outboundAuthUrl` is preferred (it is tenant-aware
 *     and known to work); fall back to the canonical UUID-based URL from
 *     `buildSmartLinkAuthUrl` if CDS omitted it.
 */
const mapSmartlinksResourceToConnectorInfo = async (
	smartlinksResource: T3pResource,
	outboundAuthPayload: OutboundAuthAccountsPayload | null,
	env: SmartLinkEnv | undefined,
): Promise<ConnectorInfo> => {
	const derivedProductKey = deriveProductKeyFromSmartlinksResourceType(smartlinksResource.type);
	const productKey = normalizeSmartlinksProductKey(derivedProductKey);
	const staticConfig = isValidProductKey(productKey)
		? await ThirdPartyConfigs.get(productKey)
		: undefined;

	// Prefer outbound-auth ground truth when we have a payload AND a canonical
	// container mapping for this productKey. Otherwise, defer to whatever CDS
	// said (`isUserOAuthed`) so we don't regress existing happy-path behaviour.
	const isAuthenticated =
		outboundAuthPayload && hasSmartLinkContainerMapping(productKey, env)
			? isAuthenticatedForProduct(outboundAuthPayload, productKey, env)
			: smartlinksResource.isUserOAuthed;

	const authUrl =
		smartlinksResource.outboundAuthUrl || buildSmartLinkAuthUrl(productKey, env) || '';

	return {
		productKey,
		displayName: String(staticConfig?.displayName || productKey),
		authUrl,
		icon: staticConfig?.icon,
		requiresOauth: staticConfig?.requiresOauth ?? true,
		isAuthenticated,
	};
};

/**
 * Fetches third-party connector resources from the Smart Link connector API and
 * returns up to `maxConnectors` connectors for display in account setup.
 *
 * IMPORTANT: Only resources whose type starts with 'smartlinks-connector-' are
 * included. This guarantees that the authUrl comes strictly from the Smart Link
 * connector, not a full connector that may override it.
 *
 * If the API returns zero valid smartlinks connector resources, a hardcoded
 * fallback set of apps (Google Drive, OneDrive, GitHub) is returned instead,
 * sliced to `maxConnectors`. On error, the same fallback set is returned.
 *
 * When `aaId` is provided, the hook also fetches the outbound-auth gateway in
 * parallel and uses its `status` field as the ground truth for
 * `isAuthenticated`. This fixes connection-state accuracy on free-tier and
 * brand-new tenants where the CDS endpoint is unreliable (edition-gated and
 * lazy-init). When `aaId` is omitted, the hook behaves exactly as before
 * (backwards-compatible).
 *
 * @param cloudId        - The Atlassian cloud ID for the current site.
 * @param userId         - Optional user ID for cache keying.
 * @param aaId           - Optional Atlassian Account ID. When provided,
 *                         outbound-auth is used as the source of truth for
 *                         connection state.
 * @param maxConnectors  - Maximum number of connectors to return (default 3).
 */
export const use3pConnectorResources = ({
	cloudId,
	userId,
	aaId,
	maxConnectors = DEFAULT_MAX_CONNECTORS,
	smartlinkConnectorRecommendationInputs,
}: {
	aaId?: string;
	cloudId: string;
	maxConnectors?: number;
	smartlinkConnectorRecommendationInputs?: SmartlinkConnectorRecommendationInputs;
	userId?: string;
}): Use3pConnectorResourcesResult => {
	const [connectorResources, setConnectorResources] = useState<ConnectorInfo[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [outboundAuthPayload, setOutboundAuthPayload] =
		useState<OutboundAuthAccountsPayload | null>(null);
	// Smart Link environment detected from the outbound-auth response. Stored
	// in state so the reconcile useEffect can pass it through to the helpers
	// without having to re-detect on every render.
	const [smartLinkEnv, setSmartLinkEnv] = useState<SmartLinkEnv | undefined>(undefined);
	const siteWideSmartlinkAppCounts =
		smartlinkConnectorRecommendationInputs?.siteWideSmartlinkAppCounts;
	const teamTypeSmartlinkAppCounts =
		smartlinkConnectorRecommendationInputs?.teamTypeSmartlinkAppCounts;
	// Full 3-tier cascade so cold-start tenants get the team-type-curated
	// Tier 3 row (or the `na` floor if no team type is known) directly from
	// the canonical recommendation source — no parallel "default app list"
	// is maintained inside the hook.
	const recommendedProductKeys = useMemo(
		() =>
			resolveRecommendedProductKeys(
				{
					siteWideSmartlinkAppCounts,
					teamTypeSmartlinkAppCounts,
					teamTypeKey: smartlinkConnectorRecommendationInputs?.teamTypeKey,
				},
				maxConnectors,
			),
		[
			siteWideSmartlinkAppCounts,
			teamTypeSmartlinkAppCounts,
			smartlinkConnectorRecommendationInputs?.teamTypeKey,
			maxConnectors,
		],
	);

	useEffect(() => {
		setIsLoading(true);

		(async () => {
			// Hoisted so the `catch` block can reuse whatever the outbound-auth
			// fetch returned in the `try` block without re-issuing the request.
			// `fetchAccountsByAaId` already de-dupes via its 30-min in-memory
			// cache, so a second call within the catch would just hit the cache,
			// but reusing the existing reference keeps the control flow clearer
			// and avoids relying on cache-as-de-dup.
			let fetchedOutboundAuth: OutboundAuthAccountsPayload | null = null;

			try {
				// Fan out CDS + outbound-auth in parallel using `Promise.allSettled`
				// so a CDS failure (very common on free-tier / brand-new tenants
				// where CDS is edition-gated or lazy-init) does NOT discard the
				// outbound-auth payload. With `Promise.all`, a CDS rejection
				// would unwind us straight into the catch block before we even
				// observed the outbound-auth result, defeating the entire
				// purpose of having ground truth.
				//
				// The shared `fetchAccountsByAaId` client caches by aaId for
				// 30 minutes, so co-mounted callers (e.g. the post-office
				// banner + onboarding modal in the same session) hit the cache
				// for free — this is not "double" the network requests, even
				// when both surfaces render on the same page.
				const [cdsResult, outboundAuthSettled] = await Promise.allSettled([
					get3pSearchConfig({
						cloudId,
						userId,
						expiration: 1000 * 60,
						returnExpired: true,
						onlyRefreshIfExpired: true,
					}),
					fetchOutboundAuthAccounts(aaId),
				]);

				fetchedOutboundAuth =
					outboundAuthSettled.status === 'fulfilled' ? outboundAuthSettled.value : null;

				// Detect the Smart Link environment from the gateway response
				// itself rather than from `window.location.hostname`. UUIDs in
				// the response are env-scoped, so this is deterministic and
				// avoids the localhost / branch-deploy ambiguity (where
				// hostname says "staging" but the local dev server proxies to
				// the real prod gateway). When the payload contains no
				// recognised UUIDs in either map, the helpers fall back to
				// hostname-based detection internally.
				const detectedEnv = detectEnvFromPayload(fetchedOutboundAuth) ?? undefined;

				setOutboundAuthPayload(fetchedOutboundAuth);
				setSmartLinkEnv(detectedEnv);

				if (cdsResult.status === 'rejected') {
					// CDS failed — fall through to the fallback path but with
					// the outbound-auth ground truth we successfully fetched.
					const fallbackConnectors = await buildFallbackConnectors(
						maxConnectors,
						fetchedOutboundAuth,
						recommendedProductKeys,
						detectedEnv,
					);
					setConnectorResources(fallbackConnectors);
				} else {
					const allResources = cdsResult.value;
					const smartlinksConnectorResources = orderSmartlinksConnectorResources(
						filterSmartlinksConnectorResources(allResources),
						recommendedProductKeys,
						maxConnectors,
					);

					if (smartlinksConnectorResources.length === 0) {
						const fallbackConnectors = await buildFallbackConnectors(
							maxConnectors,
							fetchedOutboundAuth,
							recommendedProductKeys,
							detectedEnv,
						);
						setConnectorResources(fallbackConnectors);
					} else {
						const resolvedConnectors = await Promise.all(
							smartlinksConnectorResources.map((resource) =>
								mapSmartlinksResourceToConnectorInfo(
									resource,
									fetchedOutboundAuth,
									detectedEnv,
								),
							),
						);
						setConnectorResources(resolvedConnectors);
					}
				}
			} catch {
				// Defensive — should be unreachable because `Promise.allSettled`
				// never rejects and `fetchOutboundAuthAccounts` is non-throwing.
				// Kept so any downstream throw (e.g. a render-time error in
				// `mapSmartlinksResourceToConnectorInfo`) still degrades
				// gracefully instead of leaving the modal stuck on a spinner.
				setOutboundAuthPayload(fetchedOutboundAuth);
				const fallbackEnv = detectEnvFromPayload(fetchedOutboundAuth) ?? undefined;
				const fallbackConnectors = await buildFallbackConnectors(
					maxConnectors,
					fetchedOutboundAuth,
					recommendedProductKeys,
					fallbackEnv,
				);
				setConnectorResources(fallbackConnectors);
			} finally {
				setIsLoading(false);
			}
		})();
	}, [cloudId, userId, aaId, maxConnectors, recommendedProductKeys]);

	const onConnectorConnected = useCallback(
		(productKey: string) => {
			// Optimistic UI update — flip the local row immediately so the
			// connecting user sees "Connected" without waiting for the network.
			setConnectorResources((currentResources) =>
				currentResources.map((resource) =>
					resource.productKey === productKey ? { ...resource, isAuthenticated: true } : resource,
				),
			);

			if (userId) {
				void update3pConfigCacheEntry(userId, cloudId, (config) =>
					config.map((resource) =>
						isMatchingConnectorResource(resource, productKey)
							? { ...resource, isUserOAuthed: true }
							: resource,
					),
				);
			}

			// Re-fetch outbound-auth to converge with ground truth — this catches
			// cases where the optimistic flip was wrong (e.g. user closed the
			// OAuth popup without completing) and also back-populates other
			// providers that may have changed in another tab.
			//
			// `forceRefresh: true` bypasses the 30-min in-memory cache so we
			// observe the freshly-completed OAuth handshake — without it, the
			// cached pre-OAuth payload would be returned and the reconcile
			// effect would revert the optimistic flip back to "Not Connected".
			if (aaId) {
				void fetchOutboundAuthAccounts(aaId, { forceRefresh: true }).then((payload) => {
					if (payload) {
						setOutboundAuthPayload(payload);
					}
				});
			}
		},
		[cloudId, userId, aaId],
	);

	// Whenever the outbound-auth payload changes (initial fetch, post-OAuth
	// converge re-fetch, or any other reason `setOutboundAuthPayload` ran),
	// reconcile the `isAuthenticated` field on each connector against ground
	// truth. This is what makes the post-OAuth re-fetch in
	// `onConnectorConnected` actually surface:
	//   * a true ground-truth flip when the optimistic update was wrong
	//     (e.g. the user closed the OAuth popup without completing it), and
	//   * back-population for other providers that changed in another tab.
	//
	// We only reconcile rows whose `productKey` has a canonical container
	// mapping — for everything else outbound-auth has no opinion, so we
	// must keep whatever CDS originally said. The shallow diff prevents an
	// infinite render loop when nothing actually changed.
	useEffect(() => {
		if (!outboundAuthPayload) {
			return;
		}
		setConnectorResources((currentResources) => {
			let didChange = false;
			const next = currentResources.map((resource) => {
				if (!hasSmartLinkContainerMapping(resource.productKey, smartLinkEnv)) {
					return resource;
				}
				const groundTruth = isAuthenticatedForProduct(
					outboundAuthPayload,
					resource.productKey,
					smartLinkEnv,
				);
				if (groundTruth === resource.isAuthenticated) {
					return resource;
				}
				didChange = true;
				return { ...resource, isAuthenticated: groundTruth };
			});
			return didChange ? next : currentResources;
		});
	}, [outboundAuthPayload, smartLinkEnv]);

	return { connectorResources, isLoading, onConnectorConnected };
};
